import { useCallback, useMemo } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { callPymbleApi } from "./usePymbleApi";
import {
	Attendee,
	AttendeeAddResult,
	AttendeeDecision,
	AttendeeListPage,
	AttendeeWriteResult,
} from "../types/event";

/**
 * The event's roll, and the four calls that change it.
 *
 * ⚠️ PII. Every row carries a student's name and Edumate number plus each
 * carer's name, email and phone. Rules that apply throughout this file:
 *   - nothing here is written to localStorage, sessionStorage or the URL
 *   - no attendee data reaches console.*, including in error branches — an API
 *     error can quote the request or the record back, so failures are reported
 *     by their status alone and the message shown to the user is written here
 *   - the React Query cache is in-memory only, which is where the roll stays
 *
 * | Verb   | Path                                        | Auth         |
 * |--------|---------------------------------------------|--------------|
 * | GET    | /event/admin/{event_id}/attendees/          | active staff |
 * | POST   | /event/admin/{event_id}/attendees/          | event_admin  |
 * | PATCH  | /event/admin/{event_id}/attendees/{contact} | event_admin  |
 * | DELETE | /event/admin/{event_id}/attendees/{contact} | event_admin  |
 *
 * Reading is staff-wide but writing needs `event_admin`, so the caller passes
 * `canEdit` down to its controls. The API enforces this independently — the UI
 * gate is there to avoid offering an action that will 403, not to provide the
 * security (see CLAUDE.md).
 */

const EVENT_END_POINT = "event/admin/";

/**
 * The API's own ceiling (`MAX_PAGE_SIZE`), so the roll arrives in as few round
 * trips as it can.
 */
const PAGE_SIZE = 100;

/**
 * Stop after this many pages — 5,000 attendees, far past any real event.
 *
 * A `Total` that never agrees with what is returned would otherwise spin
 * forever, and a runaway loop against an endpoint that returns student records
 * is worth ruling out explicitly rather than trusting the server to be right.
 */
const MAX_PAGES = 50;

/** What a mutation reports back so the caller can warn about a managed roll. */
export interface AttendeeMutationOutcome {
	syncManaged: boolean;
	source: string;
}

export interface AttendeeAddOutcome extends AttendeeMutationOutcome {
	added: string[];
	alreadyLinked: string[];
	unknown: string[];
}

export function useEventAttendees(eventId?: string) {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();
	const queryClient = useQueryClient();

	const base = `${EVENT_END_POINT}${eventId}/attendees/`;

	/** The cache entry the four calls share. */
	const queryKey = useMemo(() => ["event", eventId, "attendees"], [eventId]);

	/**
	 * The whole roll, gathered page by page.
	 *
	 * The endpoint pages at 100 and this tab needs all of it at once: the search
	 * box, the counts and the Excel export each work across the full list, and
	 * paging the table would quietly change all three into "…of the page you are
	 * looking at". Events run to a few hundred attendees at most, so this is one
	 * or two extra requests, not a scaling problem.
	 */
	const listQuery = useQuery({
		queryKey,
		queryFn: async (): Promise<Attendee[]> => {
			const collected: Attendee[] = [];

			for (let page = 1; page <= MAX_PAGES; page++) {
				const res: AttendeeListPage | null = await callPymbleApi(
					instance,
					"GET",
					`${base}?page=${page}&limit=${PAGE_SIZE}`
				);
				// Null means MSAL has no account yet, not an empty roll.
				if (!res) return collected;

				collected.push(...(res.Attendees ?? []));

				const total = typeof res.Total === "number" ? res.Total : 0;
				if (collected.length >= total || !res.Attendees?.length) break;
			}

			return collected;
		},
		enabled: isAuthenticated && !!eventId,
	});

	/** Re-read the roll after any write, so the table shows what was stored. */
	const refresh = useCallback(
		() => queryClient.invalidateQueries({ queryKey }),
		[queryClient, queryKey]
	);

	/**
	 * Add attendees.
	 *
	 * Takes up to 100 ContactPersonal ids. A partial success is normal and is
	 * NOT an error: an unknown id is reported and skipped while the rest are
	 * written, so the outcome is handed back for the caller to summarise rather
	 * than collapsed into "worked" or "failed".
	 */
	const addMutation = useMutation({
		mutationFn: async (input: {
			contactIds: string[];
			type?: "Attendee" | "Supervisor";
		}): Promise<AttendeeAddOutcome> => {
			const res: AttendeeAddResult | null = await callPymbleApi(
				instance,
				"POST",
				base,
				{
					data: {
						ContactIds: input.contactIds,
						Type: input.type ?? "Attendee",
					},
				}
			);
			return {
				added: res?.Added ?? [],
				alreadyLinked: res?.AlreadyLinked ?? [],
				unknown: res?.Unknown ?? [],
				syncManaged: !!res?.SyncManaged,
				source: res?.Source ?? "MyPymble",
			};
		},
		onSuccess: refresh,
	});

	/**
	 * Set one attendee's decision and/or form state.
	 *
	 * `Decision: null` clears it back to undecided, which is a different thing
	 * from not sending the field at all — the API only touches what it is given.
	 * So the key has to be present-and-null, which is why the caller passes an
	 * explicit patch rather than this hook building one from optional arguments.
	 *
	 * Recording a decision here is an administrative override of something a
	 * carer would normally answer. The API stamps it with the staff member who
	 * made it.
	 */
	const updateMutation = useMutation({
		mutationFn: async (input: {
			contactId: string;
			patch: { Decision?: AttendeeDecision | null; FormSubmitted?: boolean };
		}): Promise<AttendeeMutationOutcome> => {
			const res: AttendeeWriteResult | null = await callPymbleApi(
				instance,
				"PATCH",
				`${base}${input.contactId}`,
				{ data: input.patch }
			);
			return {
				syncManaged: !!res?.SyncManaged,
				source: res?.Source ?? "MyPymble",
			};
		},
		onSuccess: refresh,
	});

	/** Remove an attendee. Any decision recorded against the event goes with it. */
	const removeMutation = useMutation({
		mutationFn: async (contactId: string): Promise<AttendeeMutationOutcome> => {
			const res: AttendeeWriteResult | null = await callPymbleApi(
				instance,
				"DELETE",
				`${base}${contactId}`
			);
			return {
				syncManaged: !!res?.SyncManaged,
				source: res?.Source ?? "MyPymble",
			};
		},
		onSuccess: refresh,
	});

	return {
		attendees: listQuery.data ?? [],
		attendeesLoading: listQuery.isPending,
		attendeesError: listQuery.error,
		refreshAttendees: refresh,

		addAttendees: addMutation.mutateAsync,
		attendeesAdding: addMutation.isPending,

		updateAttendee: updateMutation.mutateAsync,
		attendeeUpdating: updateMutation.isPending,

		removeAttendee: removeMutation.mutateAsync,
		attendeeRemoving: removeMutation.isPending,
	};
}
